import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Injector } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { NavigationStart, Router, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import { SeoService } from '@core/seo/seo.service';
import { MAIN_CONTENT_ID } from '@core/app/app.constants';


/**
 * Sets the document title from the `title` route property (an i18n key) as "<page> — <module>".
 * On a section change (first url segment) it moves focus to #main-content, if the layout has one.
 * The new title is announced to screen readers on every navigation except the initial one.
 */
@Injectable({
    providedIn: 'root',
})
export class BoTitleStrategy extends TitleStrategy {
    readonly #seo: SeoService = inject(SeoService);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #announcer: LiveAnnouncer = inject(LiveAnnouncer);
    readonly #document: Document = inject(DOCUMENT);
    // Router is resolved lazily: it depends on TitleStrategy, so injecting it here would be circular
    readonly #injector: Injector = inject(Injector);

    #isListening: boolean = false;
    #lastSection: string = null;

    public override updateTitle(snapshot: RouterStateSnapshot): void {
        this.listenNavigationStart();

        const isInitial: boolean = this.#lastSection === null;
        const section: string = this.getSection(snapshot.url);
        const isSectionChanged: boolean = !isInitial && section !== this.#lastSection;
        const titleKey: string | undefined = this.buildTitle(snapshot);

        this.#lastSection = section;

        if (titleKey) {
            this.#translate.get(titleKey).pipe(take(1)).subscribe((title: string) => {
                this.#seo.setPageTitle(title);
                this.announce(isInitial);
            });
        } else if (!this.#seo.isDynamicTitle) {
            this.#seo.setPageTitle(null);
            this.announce(isInitial);
        }

        if (isSectionChanged) {
            this.#document.getElementById(MAIN_CONTENT_ID)?.focus({ preventScroll: true });
        }
    }

    /**
     * Titles set by components are valid for one navigation only
     */
    private listenNavigationStart(): void {
        if (this.#isListening) {
            return;
        }

        this.#isListening = true;
        this.#injector.get(Router).events
            .pipe(filter((event: unknown) => event instanceof NavigationStart))
            .subscribe(() => this.#seo.resetDynamicTitle());
    }

    private announce(isInitial: boolean): void {
        if (!isInitial) {
            void this.#announcer.announce(this.#seo.title, 'polite');
        }
    }

    private getSection(url: string): string {
        return url.split(/[?#]/)[0].split('/').find(Boolean) ?? '';
    }
}
